import React, { useState, useMemo, useCallback, useEffect } from 'react';
import axios from 'axios';
import config from '../config';
import { Settings, Globe } from 'lucide-react';
import { useSelectedMetrics, isMetricSelected, getCosClassName, getCpaClassName, useComponentMetrics } from '../utils/metricsUtils';
import MetricsConfigModal from './MetricsConfigModal';
import ModuleVisibilityToggle from './ModuleVisibilityToggle';

import { calculateVariation, renderVariation } from '../utils/variationUtils';
import { colonnesVideoGoogleAds, cumulerVideo, TOTAUX_VIDEO_INITIAUX } from '../utils/metriquesVideo';
import { getDateRangeFromPeriod } from '../utils/periodUtils';
/**
 * Composant table modulaire des réseaux Google Ads
 * Utilise l'API Google Ads segments.ad_network_type v21
 * Style identique à CampaignsTable
 */
// Couleur stable par réseau. Elle est indexée sur le code du réseau, et non sur la
// position de la ligne : un tri de colonne ou un changement de page ne redistribue
// donc pas les pastilles. Même palette que les tableaux Appareils et Réseaux Microsoft.
const NETWORK_COLORS = ['#0088FE', '#82ca9d', '#FFBB28', '#FF8042', '#8884d8'];
const NETWORK_COLOR_ORDER = [2, 3, 4, 8, 11, 12, 13, 7, 9, 10, 0, 1];
const getNetworkColor = (networkCode) => {
  const position = NETWORK_COLOR_ORDER.indexOf(Number(networkCode));
  const index = position >= 0 ? position : NETWORK_COLOR_ORDER.length;
  return NETWORK_COLORS[index % NETWORK_COLORS.length];
};

const NetworksTableModular = React.memo(({
  selectedAccount,
  selectedPeriod,
  customStartDate,
  customEndDate,
  formatNumber,
  isVisible = true,
  onToggleVisibility,
  selectedCampaignFilter,
  selectedCampaignTypeFilter,
  campaignsData = [],
  token,
  setError,
  // Props pour filtrage par labels
  selectedLabelsFilter = [],
  campaignMatchesLabelsFilter = () => true,
  // Props pour comparaison de périodes
  previousPeriodData = null,
  compareEnabled = false,
  comparisonType = '',
  loadingComparison = false,
  userRole = 'USER',
  isClientModuleVisible,
  toggleClientModuleVisibility,
  moduleId = 'networks',
  preloadedData = null
}) => {
  // États locaux
  const [networksData, setNetworksData] = useState([]);
  const [detailedNetworksData, setDetailedNetworksData] = useState([]);
  const [previousNetworksData, setPreviousNetworksData] = useState([]);
  const [previousDetailedNetworksData, setPreviousDetailedNetworksData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingPreviousPeriod, setLoadingPreviousPeriod] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [sortConfig, setSortConfig] = useState({ key: 'cost', direction: 'desc' });
  const [showMetricsConfig, setShowMetricsConfig] = useState(false);

  // Configuration des métriques pour ce composant
  const componentMetrics = useComponentMetrics('networks', selectedAccount?.id || 'global');

  // Helper pour vérifier si une métrique est sélectionnée pour ce composant
  const isComponentMetricSelected = useCallback((metricKey) => {
    return componentMetrics.metrics.includes(metricKey);
  }, [componentMetrics.metrics]);



  // Configuration du rendu des métriques (utilisé pour l'ordre dynamique)
  const metricConfig = useMemo(() => ({
    impressions: {
      label: 'Impressions',
      sortKey: 'impressions',
      renderCell: (network, prevNetwork, hasComparison) => (
        <>
          {formatNumber(network.impressions || 0)}
          {hasComparison && renderVariation(calculateVariation(network.impressions || 0, prevNetwork.impressions || 0))}
        </>
      ),
      renderTotal: (totals, previousTotals) => (
        <>
          <strong>{formatNumber(totals.impressions)}</strong>
          {previousTotals && renderVariation(calculateVariation(totals.impressions, previousTotals.impressions))}
        </>
      )
    },
    ctr: {
      label: 'CTR',
      sortKey: 'ctr',
      renderCell: (network, prevNetwork, hasComparison) => (
        <>
          {formatNumber(network.ctr || 0, 2)}%
          {hasComparison && renderVariation(calculateVariation(network.ctr || 0, prevNetwork.ctr || 0))}
        </>
      ),
      renderTotal: (totals, previousTotals) => (
        <>
          <strong>{formatNumber(totals.ctr, 2)}%</strong>
          {previousTotals && renderVariation(calculateVariation(totals.ctr, previousTotals.ctr))}
        </>
      )
    },
    clics: {
      label: 'Clics',
      sortKey: 'clicks',
      renderCell: (network, prevNetwork, hasComparison) => (
        <>
          {formatNumber(network.clicks || 0)}
          {hasComparison && renderVariation(calculateVariation(network.clicks || 0, prevNetwork.clicks || 0))}
        </>
      ),
      renderTotal: (totals, previousTotals) => (
        <>
          <strong>{formatNumber(totals.clicks)}</strong>
          {previousTotals && renderVariation(calculateVariation(totals.clicks, previousTotals.clicks))}
        </>
      )
    },
    cpc: {
      label: 'CPC',
      sortKey: 'cpc',
      renderCell: (network, prevNetwork, hasComparison) => (
        <>
          €{formatNumber(network.cpc || 0, 2)}
          {hasComparison && renderVariation(calculateVariation(network.cpc || 0, prevNetwork.cpc || 0), true)}
        </>
      ),
      renderTotal: (totals, previousTotals) => (
        <>
          <strong>€{formatNumber(totals.cpc, 2)}</strong>
          {previousTotals && renderVariation(calculateVariation(totals.cpc, previousTotals.cpc), true)}
        </>
      )
    },
    cout: {
      label: 'Coût',
      sortKey: 'cost',
      renderCell: (network, prevNetwork, hasComparison) => (
        <>
          €{formatNumber(network.cost || 0, 2)}
          {hasComparison && renderVariation(calculateVariation(network.cost || 0, prevNetwork.cost || 0), true)}
        </>
      ),
      renderTotal: (totals, previousTotals) => (
        <>
          <strong>€{formatNumber(totals.cost, 2)}</strong>
          {previousTotals && renderVariation(calculateVariation(totals.cost, previousTotals.cost), true)}
        </>
      )
    },
    taux_conv: {
      label: 'Taux conv.',
      sortKey: 'conversionRate',
      renderCell: (network, prevNetwork, hasComparison) => {
        const conversionRate = network.clicks > 0 ? (network.conversions / network.clicks * 100) : 0;
        const prevConversionRate = prevNetwork && prevNetwork.clicks > 0 ? (prevNetwork.conversions / prevNetwork.clicks * 100) : 0;
        return (
          <>
            {formatNumber(conversionRate, 2)}%
            {hasComparison && renderVariation(calculateVariation(conversionRate, prevConversionRate))}
          </>
        );
      },
      renderTotal: (totals, previousTotals) => (
        <>
          <strong>{formatNumber(totals.conversionRate, 2)}%</strong>
          {previousTotals && renderVariation(calculateVariation(totals.conversionRate, previousTotals.conversionRate))}
        </>
      )
    },
    conv: {
      label: 'Conv.',
      sortKey: 'conversions',
      renderCell: (network, prevNetwork, hasComparison) => (
        <>
          {formatNumber(network.conversions || 0, 1)}
          {hasComparison && renderVariation(calculateVariation(network.conversions || 0, prevNetwork.conversions || 0))}
        </>
      ),
      renderTotal: (totals, previousTotals) => (
        <>
          <strong>{formatNumber(totals.conversions, 1)}</strong>
          {previousTotals && renderVariation(calculateVariation(totals.conversions, previousTotals.conversions))}
        </>
      )
    },
    cpa: {
      label: 'CPA',
      sortKey: 'cpa',
      getClassName: (network) => {
        const cpa = network.conversions > 0 ? (network.cost / network.conversions) : 0;
        return getCpaClassName(cpa, selectedAccount?.id || 'global', moduleId);
      },
      getTotalClassName: (totals) => getCpaClassName(totals.cpa, selectedAccount?.id || 'global', moduleId),
      renderCell: (network, prevNetwork, hasComparison) => {
        const cpa = network.conversions > 0 ? (network.cost / network.conversions) : 0;
        const prevCpa = prevNetwork && prevNetwork.conversions > 0 ? (prevNetwork.cost / prevNetwork.conversions) : 0;
        return (
          <>
            €{formatNumber(cpa || 0, 2)}
            {hasComparison && renderVariation(calculateVariation(cpa, prevCpa), true)}
          </>
        );
      },
      renderTotal: (totals, previousTotals) => (
        <>
          <strong>€{formatNumber(totals.cpa || 0, 2)}</strong>
          {previousTotals && renderVariation(calculateVariation(totals.cpa, previousTotals.cpa), true)}
        </>
      )
    },
    valeur_conv: {
      label: 'Valeur Conv.',
      sortKey: 'conversionsValue',
      renderCell: (network, prevNetwork, hasComparison) => (
        <>
          €{formatNumber(network.conversionsValue || 0, 2)}
          {hasComparison && renderVariation(calculateVariation(network.conversionsValue || 0, prevNetwork.conversionsValue || 0))}
        </>
      ),
      renderTotal: (totals, previousTotals) => (
        <>
          <strong>€{formatNumber(totals.conversionsValue, 2)}</strong>
          {previousTotals && renderVariation(calculateVariation(totals.conversionsValue, previousTotals.conversionsValue))}
        </>
      )
    },
    cos: {
      label: 'COS',
      sortKey: 'cos',
      getClassName: (network) => {
        const cos = (network.conversionsValue || 0) > 0 ? (network.cost / (network.conversionsValue || 1) * 100) : 0;
        return getCosClassName(cos, selectedAccount?.id || 'global', moduleId);
      },
      getTotalClassName: (totals) => getCosClassName(totals.cos, selectedAccount?.id || 'global', moduleId),
      renderCell: (network, prevNetwork, hasComparison) => {
        const cos = (network.conversionsValue || 0) > 0 ? (network.cost / (network.conversionsValue || 1) * 100) : 0;
        const prevCos = prevNetwork && (prevNetwork.conversionsValue || 0) > 0 ? (prevNetwork.cost / (prevNetwork.conversionsValue || 1) * 100) : 0;
        return (
          <>
            {formatNumber(cos, 1)}%
            {hasComparison && renderVariation(calculateVariation(cos, prevCos), true)}
          </>
        );
      },
      renderTotal: (totals, previousTotals) => (
        <>
          <strong>{formatNumber(totals.cos, 1)}%</strong>
          {previousTotals && renderVariation(calculateVariation(totals.cos, previousTotals.cos), true)}
        </>
      )
    },
    roas: {
      label: 'ROAS',
      sortKey: 'roas',
      renderCell: (network, prevNetwork, hasComparison) => {
        const roas = (network.cost || 0) > 0 ? ((network.conversionsValue || 0) / network.cost * 100) : 0;
        const prevRoas = prevNetwork && (prevNetwork.cost || 0) > 0 ? ((prevNetwork.conversionsValue || 0) / prevNetwork.cost * 100) : 0;
        return (
          <>
            {formatNumber(roas, 1)}%
            {hasComparison && renderVariation(calculateVariation(roas, prevRoas))}
          </>
        );
      },
      renderTotal: (totals, previousTotals) => {
        const roas = (totals.cost || 0) > 0 ? ((totals.conversionsValue || 0) / totals.cost * 100) : 0;
        const prevRoas = previousTotals && (previousTotals.cost || 0) > 0 ? ((previousTotals.conversionsValue || 0) / previousTotals.cost * 100) : 0;
        return (
          <>
            <strong>{formatNumber(roas, 1)}%</strong>
            {previousTotals && renderVariation(calculateVariation(roas, prevRoas))}
          </>
        );
      }
    },
    roi: {
      label: 'ROI',
      sortKey: 'roi',
      renderCell: (network, prevNetwork, hasComparison) => {
        const roi = (network.cost || 0) > 0 ? (((network.conversionsValue || 0) - network.cost) / network.cost) : 0;
        const prevRoi = prevNetwork && (prevNetwork.cost || 0) > 0 ? (((prevNetwork.conversionsValue || 0) - prevNetwork.cost) / prevNetwork.cost) : 0;
        return (
          <>
            {formatNumber(roi, 2)}
            {hasComparison && renderVariation(calculateVariation(roi, prevRoi))}
          </>
        );
      },
      renderTotal: (totals, previousTotals) => {
        const roi = (totals.cost || 0) > 0 ? (((totals.conversionsValue || 0) - totals.cost) / totals.cost) : 0;
        const prevRoi = previousTotals && (previousTotals.cost || 0) > 0 ? (((previousTotals.conversionsValue || 0) - previousTotals.cost) / previousTotals.cost) : 0;
        return (
          <>
            <strong>{formatNumber(roi, 2)}</strong>
            {previousTotals && renderVariation(calculateVariation(roi, prevRoi))}
          </>
        );
      }
    },
    panier_moyen: {
      label: 'Panier moyen',
      sortKey: 'panierMoyen',
      renderCell: (network, prevNetwork, hasComparison) => {
        const panierMoyen = (network.conversions || 0) > 0 ? (network.conversionsValue || 0) / network.conversions : 0;
        const prevPanierMoyen = prevNetwork && (prevNetwork.conversions || 0) > 0 ? (prevNetwork.conversionsValue || 0) / prevNetwork.conversions : 0;
        return (
          <>
            {formatNumber(panierMoyen, 2)}
            {hasComparison && renderVariation(calculateVariation(panierMoyen, prevPanierMoyen))}
          </>
        );
      },
      renderTotal: (totals, previousTotals) => {
        const panierMoyen = (totals.conversions || 0) > 0 ? (totals.conversionsValue || 0) / totals.conversions : 0;
        const prevPanierMoyen = previousTotals && (previousTotals.conversions || 0) > 0 ? (previousTotals.conversionsValue || 0) / previousTotals.conversions : 0;
        return (
          <>
            <strong>{formatNumber(panierMoyen, 2)}</strong>
            {previousTotals && renderVariation(calculateVariation(panierMoyen, prevPanierMoyen))}
          </>
        );
      }
    },
    ...colonnesVideoGoogleAds({ formatNumber, renderVariation, calculateVariation })
  }), [formatNumber, selectedAccount?.id, renderVariation, calculateVariation]);

  // Récupérer les métriques sélectionnées avec mise à jour automatique
  const selectedMetrics = useSelectedMetrics();

  // Helper pour formater une date en YYYY-MM-DD (formatage LOCAL, pas UTC)
  const formatLocalDate = useCallback((date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }, []);



  // Chargement des données
  useEffect(() => {
    if (!isVisible || !selectedAccount?.id) {
      setNetworksData([]);
      setDetailedNetworksData([]);
      setPreviousNetworksData([]);
      setPreviousDetailedNetworksData([]);
      return;
    }

    // Si preloadedData disponible et pas de filtres actifs, l'utiliser directement
    if (preloadedData?.success && !selectedCampaignFilter && !selectedCampaignTypeFilter) {
      console.log(`🌐 [NETWORKS-FRONTEND] Utilisation données préchargées`);
      const networks = preloadedData.data?.networks || [];
      const detailedNetworks = preloadedData.data?.detailedNetworks || [];
      console.log(`🌐 [NETWORKS-FRONTEND] ${networks.length} réseaux préchargés, ${detailedNetworks.length} lignes détaillées`);
      setNetworksData(networks);
      setDetailedNetworksData(detailedNetworks);
      return;
    }

    const loadNetworksData = async () => {
      console.log(`🌐 [NETWORKS-FRONTEND] Chargement données pour ${selectedAccount.name}`);
      setLoading(true);

      try {
        // Calculer les dates de la période actuelle en LOCAL (évite décalage fuseau horaire avec le serveur)
        let currentStartDate = customStartDate;
        let currentEndDate = customEndDate;

        // Si pas de dates custom, les calculer à partir de selectedPeriod
        if (!currentStartDate || !currentEndDate) {
          const dateRange = getDateRangeFromPeriod(selectedPeriod);
          currentStartDate = dateRange.startDate;
          currentEndDate = dateRange.endDate;
        }

        console.log(`🌐 [NETWORKS-FRONTEND] Période actuelle: ${currentStartDate} à ${currentEndDate}`);

        // Charger les données de la période actuelle avec dates explicites
        const response = await axios.post(`${config.API_URL}/api/dashboard/components/networks`, {
          sources: [selectedAccount.id],
          period: 'CUSTOM',
          customStartDate: currentStartDate,
          customEndDate: currentEndDate,
        }, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        console.log(`🌐 [NETWORKS-FRONTEND] Réponse:`, response.data);

        if (response.data.success) {
          const networks = response.data.data?.networks || [];
          const detailedNetworks = response.data.data?.detailedNetworks || [];
          console.log(`🌐 [NETWORKS-FRONTEND] ${networks.length} réseaux, ${detailedNetworks.length} lignes détaillées`);

          setNetworksData(networks);
          setDetailedNetworksData(detailedNetworks);
        } else {
          console.error(`❌ [NETWORKS-FRONTEND] Erreur:`, response.data.error);
          setError?.(`Erreur: ${response.data.error}`);
        }
      } catch (error) {
        console.error(`❌ [NETWORKS-FRONTEND] Erreur requête:`, error);
        setError?.(error.response?.data?.error || 'Erreur lors du chargement des données réseaux');
      } finally {
        setLoading(false);
      }
    };

    loadNetworksData();
  }, [selectedAccount, selectedPeriod, customStartDate, customEndDate, token, isVisible, preloadedData, selectedCampaignFilter, selectedCampaignTypeFilter]);

  // Chargement des données de période précédente UNIQUEMENT si compareEnabled est true
  // Utilise previousPeriodData.period pour garantir la cohérence avec Types de campagnes
  useEffect(() => {
    // Si comparaison non activée, vider les données de période précédente
    if (!compareEnabled) {
      setPreviousNetworksData([]);
      setPreviousDetailedNetworksData([]);
      return;
    }

    if (!isVisible || !selectedAccount?.id) {
      setPreviousNetworksData([]);
      setPreviousDetailedNetworksData([]);
      return;
    }

    // Attendre que previousPeriodData.period soit disponible
    if (!previousPeriodData?.period?.startDate || !previousPeriodData?.period?.endDate) {
      console.log(`🌐 [NETWORKS-FRONTEND] En attente de previousPeriodData.period...`);
      return;
    }

    const loadPreviousPeriodData = async () => {
      const prevPeriod = {
        startDate: previousPeriodData.period.startDate,
        endDate: previousPeriodData.period.endDate
      };
      console.log(`🌐 [NETWORKS-FRONTEND] Chargement période précédente (depuis previousPeriodData): ${prevPeriod.startDate} à ${prevPeriod.endDate}`);

      setLoadingPreviousPeriod(true);
      try {
        const prevResponse = await axios.post(`${config.API_URL}/api/dashboard/components/networks`, {
          sources: [selectedAccount.id],
          period: 'CUSTOM',
          customStartDate: prevPeriod.startDate,
          customEndDate: prevPeriod.endDate,
        }, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (prevResponse.data.success) {
          const prevNetworks = prevResponse.data.data?.networks || [];
          const prevDetailedNetworks = prevResponse.data.data?.detailedNetworks || [];
          console.log(`🌐 [NETWORKS-FRONTEND] ${prevNetworks.length} réseaux, ${prevDetailedNetworks.length} lignes détaillées période précédente`);
          setPreviousNetworksData(prevNetworks);
          setPreviousDetailedNetworksData(prevDetailedNetworks);
        }
      } catch (prevError) {
        console.warn(`⚠️ [NETWORKS-FRONTEND] Erreur chargement période précédente:`, prevError.message);
        setPreviousNetworksData([]);
        setPreviousDetailedNetworksData([]);
      } finally {
        setLoadingPreviousPeriod(false);
      }
    };

    loadPreviousPeriodData();
  }, [selectedAccount, token, isVisible, compareEnabled, previousPeriodData?.period?.startDate, previousPeriodData?.period?.endDate, preloadedData, selectedCampaignFilter, selectedCampaignTypeFilter]);

  // Filtrer et agréger les données selon les filtres actifs
  const filteredNetworksData = useMemo(() => {
    // Si pas de filtre, utiliser les données agrégées directement
    if (!selectedCampaignFilter && !selectedCampaignTypeFilter && selectedLabelsFilter.length === 0) {
      return networksData;
    }

    // Sinon, filtrer les données détaillées et réagréger
    let filteredDetails = [...detailedNetworksData];

    // Filtrer par labels
    if (selectedLabelsFilter.length > 0) {
      filteredDetails = filteredDetails.filter(detail => {
        const detailCampaignId = detail.campaignId || detail.campaign_id;
        return campaignMatchesLabelsFilter(detailCampaignId);
      });
      console.log(`🌐 [NETWORKS-FRONTEND] Filtré par labels: ${filteredDetails.length} lignes`);
    }

    // Filtrer par campagne
    if (selectedCampaignFilter) {
      filteredDetails = filteredDetails.filter(detail => {
        return detail.campaignId === selectedCampaignFilter.id ||
               detail.campaignId === selectedCampaignFilter.campaignId ||
               detail.campaignId?.toString() === selectedCampaignFilter.id?.toString() ||
               detail.campaignName === selectedCampaignFilter.name;
      });
      console.log(`🌐 [NETWORKS-FRONTEND] Filtré par campagne "${selectedCampaignFilter.name}": ${filteredDetails.length} lignes`);
    }

    // Filtrer par type de campagne
    if (selectedCampaignTypeFilter) {
      // Trouver les IDs de campagnes correspondant au type
      const matchingCampaignIds = campaignsData
        .filter(campaign => campaign.advertisingChannelType === selectedCampaignTypeFilter.type)
        .map(campaign => (campaign.id || campaign.campaignId)?.toString());

      filteredDetails = filteredDetails.filter(detail =>
        matchingCampaignIds.includes(detail.campaignId?.toString()) ||
        detail.campaignType === selectedCampaignTypeFilter.type
      );
      console.log(`🌐 [NETWORKS-FRONTEND] Filtré par type "${selectedCampaignTypeFilter.name}": ${filteredDetails.length} lignes`);
    }

    // Réagréger par réseau
    const aggregated = {};
    filteredDetails.forEach(detail => {
      const key = detail.networkCode;
      if (!aggregated[key]) {
        aggregated[key] = {
          accountId: detail.accountId,
          accountName: detail.accountName,
          customerId: detail.customerId,
          networkCode: detail.networkCode,
          networkKey: detail.networkKey,
          networkName: detail.networkName,
          impressions: 0,
          clicks: 0,
          cost: 0,
          conversions: 0,
          conversionsValue: 0
        };
      }
      aggregated[key].impressions += detail.impressions || 0;
      aggregated[key].clicks += detail.clicks || 0;
      aggregated[key].cost += detail.cost || 0;
      aggregated[key].conversions += detail.conversions || 0;
      aggregated[key].conversionsValue += detail.conversionsValue || 0;
    });

    // Calculer les métriques dérivées
    return Object.values(aggregated).map(network => {
      const ctr = network.impressions > 0 ? (network.clicks / network.impressions * 100) : 0;
      const cpc = network.clicks > 0 ? (network.cost / network.clicks) : 0;
      const conversionRate = network.clicks > 0 ? (network.conversions / network.clicks * 100) : 0;
      const cpa = network.conversions > 0 ? (network.cost / network.conversions) : 0;
      const roas = network.cost > 0 ? (network.conversionsValue / network.cost) : 0;
      const cos = network.conversionsValue > 0 ? (network.cost / network.conversionsValue * 100) : 0;

      return {
        ...network,
        ctr,
        cpc,
        conversionRate,
        cpa,
        roas,
        cos
      };
    });
  }, [networksData, detailedNetworksData, selectedCampaignFilter, selectedCampaignTypeFilter, campaignsData, selectedLabelsFilter, campaignMatchesLabelsFilter]);

  // Filtrer les données de période précédente avec les mêmes filtres
  const filteredPreviousNetworksData = useMemo(() => {
    // Si pas de données de période précédente, retourner vide
    if (!previousNetworksData.length) return [];

    // Si pas de filtre, utiliser les données agrégées directement
    if (!selectedCampaignFilter && !selectedCampaignTypeFilter && selectedLabelsFilter.length === 0) {
      return previousNetworksData;
    }

    // Sinon, filtrer les données détaillées et réagréger
    let filteredDetails = [...previousDetailedNetworksData];

    // Filtrer par labels
    if (selectedLabelsFilter.length > 0) {
      filteredDetails = filteredDetails.filter(detail => {
        const detailCampaignId = detail.campaignId || detail.campaign_id;
        return campaignMatchesLabelsFilter(detailCampaignId);
      });
    }

    // Filtrer par campagne
    if (selectedCampaignFilter) {
      filteredDetails = filteredDetails.filter(detail => {
        return detail.campaignId === selectedCampaignFilter.id ||
               detail.campaignId === selectedCampaignFilter.campaignId ||
               detail.campaignId?.toString() === selectedCampaignFilter.id?.toString() ||
               detail.campaignName === selectedCampaignFilter.name;
      });
    }

    // Filtrer par type de campagne
    if (selectedCampaignTypeFilter) {
      const matchingCampaignIds = campaignsData
        .filter(campaign => campaign.advertisingChannelType === selectedCampaignTypeFilter.type)
        .map(campaign => (campaign.id || campaign.campaignId)?.toString());

      filteredDetails = filteredDetails.filter(detail =>
        matchingCampaignIds.includes(detail.campaignId?.toString()) ||
        detail.campaignType === selectedCampaignTypeFilter.type
      );
    }

    // Réagréger par réseau
    const aggregated = {};
    filteredDetails.forEach(detail => {
      const key = detail.networkCode;
      if (!aggregated[key]) {
        aggregated[key] = {
          networkCode: detail.networkCode,
          networkName: detail.networkName,
          impressions: 0,
          clicks: 0,
          cost: 0,
          conversions: 0,
          conversionsValue: 0
        };
      }
      aggregated[key].impressions += detail.impressions || 0;
      aggregated[key].clicks += detail.clicks || 0;
      aggregated[key].cost += detail.cost || 0;
      aggregated[key].conversions += detail.conversions || 0;
      aggregated[key].conversionsValue += detail.conversionsValue || 0;
    });

    // Calculer les métriques dérivées
    return Object.values(aggregated).map(network => ({
      ...network,
      ctr: network.impressions > 0 ? (network.clicks / network.impressions * 100) : 0,
      cpc: network.clicks > 0 ? (network.cost / network.clicks) : 0,
      conversionRate: network.clicks > 0 ? (network.conversions / network.clicks * 100) : 0,
      cpa: network.conversions > 0 ? (network.cost / network.conversions) : 0,
      cos: network.conversionsValue > 0 ? (network.cost / network.conversionsValue * 100) : 0
    }));
  }, [previousNetworksData, previousDetailedNetworksData, selectedCampaignFilter, selectedCampaignTypeFilter, campaignsData, selectedLabelsFilter, campaignMatchesLabelsFilter]);

  // Fonction de tri memoized
  const sortedNetworks = useMemo(() => {
    if (!filteredNetworksData.length) return [];

    const sorted = [...filteredNetworksData];
    sorted.sort((a, b) => {
      let aValue, bValue;

      // Calcul spécial pour CPA
      if (sortConfig.key === 'cpa') {
        aValue = a.conversions > 0 ? (a.cost / a.conversions) : Number.MAX_VALUE;
        bValue = b.conversions > 0 ? (b.cost / b.conversions) : Number.MAX_VALUE;
      } else {
        aValue = a[sortConfig.key] || 0;
        bValue = b[sortConfig.key] || 0;
      }

      if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
    return sorted;
  }, [filteredNetworksData, sortConfig]);

  // Calcul des totaux memoized
  const totals = useMemo(() => {
    if (!filteredNetworksData.length) return {};

    const baseTotals = filteredNetworksData.reduce((acc, network) => ({
      impressions: acc.impressions + (network.impressions || 0),
      clicks: acc.clicks + (network.clicks || 0),
      cost: acc.cost + (network.cost || 0),
      conversions: acc.conversions + (network.conversions || 0),
      conversionsValue: acc.conversionsValue + (network.conversionsValue || 0),
      ...cumulerVideo(acc, network)
    }), { impressions: 0, clicks: 0, cost: 0, conversions: 0, conversionsValue: 0, ...TOTAUX_VIDEO_INITIAUX });

    // Calculer les métriques dérivées
    return {
      ...baseTotals,
      ctr: baseTotals.impressions > 0 ? (baseTotals.clicks / baseTotals.impressions * 100) : 0,
      cpc: baseTotals.clicks > 0 ? (baseTotals.cost / baseTotals.clicks) : 0,
      conversionRate: baseTotals.clicks > 0 ? (baseTotals.conversions / baseTotals.clicks * 100) : 0,
      cpa: baseTotals.conversions > 0 ? (baseTotals.cost / baseTotals.conversions) : 0,
      cos: baseTotals.conversionsValue > 0 ? (baseTotals.cost / baseTotals.conversionsValue * 100) : 0
    };
  }, [filteredNetworksData]);

  // Calcul des totaux de la période précédente
  // Utiliser les données filtrées pour cohérence avec la période actuelle
  const previousTotals = useMemo(() => {
    if (!filteredPreviousNetworksData.length) return null;

    const baseTotals = filteredPreviousNetworksData.reduce((acc, network) => ({
      impressions: acc.impressions + (network.impressions || 0),
      clicks: acc.clicks + (network.clicks || 0),
      cost: acc.cost + (network.cost || 0),
      conversions: acc.conversions + (network.conversions || 0),
      conversionsValue: acc.conversionsValue + (network.conversionsValue || 0),
      ...cumulerVideo(acc, network)
    }), { impressions: 0, clicks: 0, cost: 0, conversions: 0, conversionsValue: 0, ...TOTAUX_VIDEO_INITIAUX });

    return {
      ...baseTotals,
      ctr: baseTotals.impressions > 0 ? (baseTotals.clicks / baseTotals.impressions * 100) : 0,
      cpc: baseTotals.clicks > 0 ? (baseTotals.cost / baseTotals.clicks) : 0,
      conversionRate: baseTotals.clicks > 0 ? (baseTotals.conversions / baseTotals.clicks * 100) : 0,
      cpa: baseTotals.conversions > 0 ? (baseTotals.cost / baseTotals.conversions) : 0,
      cos: baseTotals.conversionsValue > 0 ? (baseTotals.cost / baseTotals.conversionsValue * 100) : 0
    };
  }, [filteredPreviousNetworksData]);

  // Configuration de pagination
  const itemsPerPage = 20;
  const totalNetworks = sortedNetworks.length;
  const totalPages = Math.ceil(totalNetworks / itemsPerPage);

  // Réinitialiser la page quand les données changent
  useEffect(() => {
    if (currentPage > totalPages && totalPages > 0) {
      setCurrentPage(1);
    }
  }, [currentPage, totalPages]);

  // Données paginées memoized
  const paginatedNetworks = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = Math.min(startIndex + itemsPerPage, totalNetworks);
    return sortedNetworks.slice(startIndex, endIndex);
  }, [sortedNetworks, currentPage, itemsPerPage, totalNetworks]);

  // Handler de tri memoized
  const handleSort = useCallback((key) => {
    let direction = 'desc';
    if (sortConfig.key === key && sortConfig.direction === 'desc') {
      direction = 'asc';
    }
    setSortConfig({ key, direction });
    setCurrentPage(1);
  }, [sortConfig]);

  // Handler de pagination memoized
  const handlePageChange = useCallback((newPage) => {
    setCurrentPage(Math.max(1, Math.min(totalPages, newPage)));
  }, [totalPages]);

  // Ne pas rendre si pas de données
  if (!filteredNetworksData.length && !loading) return null;

  // Pour les clients : masquer complètement si le module est fermé
  if (userRole === 'CLIENT' && !isVisible) {
    return null;
  }

  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = Math.min(startIndex + itemsPerPage, totalNetworks);

  return (
    <div className="campaigns-table-section">
      <div className="section-header">
        <h4><Globe size={16} />&nbsp;Réseaux</h4>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          {/* Bouton configuration des métriques - visible uniquement pour non-clients */}
          {userRole !== 'CLIENT' && (
            <button
              onClick={() => setShowMetricsConfig(true)}
              className="metrics-settings-btn"
              title="Configurer les métriques"
            >
              <Settings size={16} />
            </button>
          )}
          {toggleClientModuleVisibility ? (
            <ModuleVisibilityToggle
              isVisible={isClientModuleVisible ? isClientModuleVisible(moduleId) : true}
              onToggle={toggleClientModuleVisibility}
              userRole={userRole}
              moduleId={moduleId}
              title="Réseaux"
            />
          ) : (
            <button
              onClick={onToggleVisibility}
              className="section-toggle-btn"
              title={isVisible ? "Masquer la section" : "Afficher la section"}
            >
              {isVisible ? '−' : '+'}
            </button>
          )}
        </div>
      </div>

      {isVisible && (
        <>
          {loading && (
            <div className="loading">Chargement des données réseaux...</div>
          )}

          {/* Indicateur de chargement des données de comparaison */}
          {!loading && compareEnabled && (loadingComparison || loadingPreviousPeriod) && (
            <div className="loading-message" style={{ textAlign: 'center', padding: '20px' }}>
              <div className="loading-spinner" style={{ margin: '0 auto 10px' }}></div>
              Chargement des données de comparaison...
            </div>
          )}

          {!loading && filteredNetworksData.length > 0 && (!compareEnabled || (!loadingComparison && !loadingPreviousPeriod)) && (
            <div className="campaigns-table-wrapper">
              <table
                key={selectedMetrics.filter(m => m.selected).map(m => m.key).join(',')}
                className="campaigns-table"
                style={{
                  width: '100%',
                  tableLayout: 'auto',
                  borderCollapse: 'collapse'
                }}>
                <thead>
                  <tr>
                    <th onClick={() => handleSort('networkName')} className="sortable">
                      Réseau {sortConfig.key === 'networkName' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                    </th>
                    {/* Rendu dynamique des colonnes selon l'ordre configuré */}
                    {componentMetrics.order.filter(key => isComponentMetricSelected(key) && metricConfig[key]).map(metricKey => {
                      const config = metricConfig[metricKey];
                      return (
                        <th key={metricKey} onClick={() => handleSort(config.sortKey)} className="sortable numeric">
                          {config.label} {sortConfig.key === config.sortKey && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                        </th>
                      );
                    })}
                  </tr>
                </thead>

                <tbody>
                  {paginatedNetworks.map((network, index) => {
                    // Trouver la ligne correspondante dans les données filtrées de la période précédente
                    const prevNetwork = filteredPreviousNetworksData.length > 0
                      ? filteredPreviousNetworksData.find(p => p.networkCode === network.networkCode)
                      : null;
                    const hasComparison = !!prevNetwork;

                    return (
                      <tr key={`${network.networkCode}-${network.accountId}-${index}`}>
                        <td className="campaign-name">
                          <span
                            className="legend-dot"
                            style={{ backgroundColor: getNetworkColor(network.networkCode) }}
                          />
                          {network.networkName}
                        </td>
                        {/* Rendu dynamique des cellules selon l'ordre configuré */}
                        {componentMetrics.order.filter(key => isComponentMetricSelected(key) && metricConfig[key]).map(metricKey => {
                          const config = metricConfig[metricKey];
                          const className = config.getClassName ? `numeric ${config.getClassName(network)}` : 'numeric';
                          return (
                            <td key={metricKey} className={className}>
                              {config.renderCell(network, prevNetwork, hasComparison)}
                            </td>
                          );
                        })}
                      </tr>
                    );
                  })}
                </tbody>

                <tfoot>
                  <tr className="totals-row">
                    <td className="campaign-name"><strong>TOTAL</strong></td>
                    {/* Rendu dynamique des totaux selon l'ordre configuré */}
                    {componentMetrics.order.filter(key => isComponentMetricSelected(key) && metricConfig[key]).map(metricKey => {
                      const config = metricConfig[metricKey];
                      const className = config.getTotalClassName ? `numeric ${config.getTotalClassName(totals)}` : 'numeric';
                      return (
                        <td key={metricKey} className={className}>
                          {config.renderTotal(totals, previousTotals)}
                        </td>
                      );
                    })}
                  </tr>
                </tfoot>
              </table>
            </div>
          )}

          {/* Contrôles de pagination */}
          {!loading && filteredNetworksData.length > 0 && totalPages > 1 && (
            <div className="pagination-controls">
              <div className="pagination-info">
                <span>
                  Affichage {startIndex + 1}-{Math.min(endIndex, sortedNetworks.length)} sur {sortedNetworks.length} réseaux
                </span>
              </div>
              <div className="pagination-buttons">
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="pagination-btn"
                >
                  ← Précédent
                </button>
                <span className="page-numbers">
                  Page {currentPage} sur {totalPages}
                </span>
                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className="pagination-btn"
                >
                  Suivant →
                </button>
              </div>
            </div>
          )}

          {!loading && filteredNetworksData.length === 0 && (
            <div className="no-data">
              Aucune donnée de réseaux disponible pour la période sélectionnée
            </div>
          )}
        </>
      )}

      {/* Modal de configuration des métriques */}
      <MetricsConfigModal
        isOpen={showMetricsConfig}
        onClose={() => setShowMetricsConfig(false)}
        componentId="networks"
        accountId={selectedAccount?.id || 'global'}
        componentTitle="Réseaux"
      />
    </div>
  );
});

NetworksTableModular.displayName = 'NetworksTableModular';

export default NetworksTableModular;
